// pages/SupportPage.js
import React, { useState, useEffect } from 'react';
import {
    Container,
    Box,
    useMediaQuery,
    useTheme,
    Tabs,
    Tab,
    Typography,
    Badge,
    Paper,
    Fade
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
    Plus, 
    History, 
    BookOpen,
    HelpCircle
} from 'lucide-react';
import { Footer } from '../components/HomePage';
import Sidebar from '../components/MyProfile/Sidebar';
import ZendeskForm from '../components/MyProfile/Support/ZendeskForm';
import TicketHistory from '../components/MyProfile/Support/TicketHistory';
import KnowledgeBaseSearch from '../components/MyProfile/Support/KnowledgeBaseSearch';
import { useAuth0 } from '@auth0/auth0-react';
import zendeskService from '../services/zendeskService';

const SupportPage = () => {
    const theme = useTheme();
    const { getAccessTokenSilently } = useAuth0();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // UI state
    const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
    const [darkMode, setDarkMode] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [openTicketsCount, setOpenTicketsCount] = useState(0);
    const [hasSearchedKB, setHasSearchedKB] = useState(false);
    
    // Update sidebar state when screen size changes
    useEffect(() => {
        setSidebarOpen(!isMobile);
    }, [isMobile]);
    
    // Fetch open tickets count for badge
    useEffect(() => {
        fetchOpenTicketsCount();
    }, []);
    
    const fetchOpenTicketsCount = async () => {
        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            const response = await zendeskService.getUserTickets({ 
                status: 'open',
                per_page: 1
            }, token);
            
            setOpenTicketsCount(response.data.count);
        } catch (error) {
            console.error('Error fetching open tickets count:', error);
        }
    };
    
    // Calculate content margin based on device and sidebar state
    const getContentMargin = () => {
        if (isMobile) return 0;
        if (sidebarOpen) return '240px';
        return '80px';
    };
    
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };
    
    const handleArticleClick = (article) => {
        // Track that user has searched KB
        setHasSearchedKB(true);
        console.log('Article clicked:', article);
    };
    
    const handleTicketCreated = () => {
        // Refresh open tickets count
        fetchOpenTicketsCount();
        // Switch to history tab
        setActiveTab(1);
    };
    
    // Tab panel component
    const TabPanel = ({ children, value, index }) => {
        return (
            <div hidden={value !== index}>
                {value === index && (
                    <Fade in={true} timeout={500}>
                        <Box sx={{ pt: 3 }}>
                            {children}
                        </Box>
                    </Fade>
                )}
            </div>
        );
    };
    
    return (
        <>
            <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: darkMode ? '#121212' : 'grey.50' }}>
                {/* Sidebar component */}
                <Sidebar 
                    onToggle={() => setSidebarOpen(!sidebarOpen)}
                    darkMode={darkMode}
                />

                <Box
                    component={motion.div}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    sx={{
                        flexGrow: 1,
                        ml: !isMobile ? getContentMargin() : 0,
                        transition: 'margin-left 0.3s',
                        p: isMobile ? 2 : 3,
                        width: '100%'
                    }}
                >
                    <Container maxWidth="lg" sx={{ px: isMobile ? 1 : 3 }}>
                        {/* Page Header */}
                        <Box sx={{ mb: 4, textAlign: 'center' }}>
                            <Box sx={{ 
                                width: 80, 
                                height: 80, 
                                borderRadius: '50%', 
                                bgcolor: '#FF4775',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                mb: 2,
                                boxShadow: '0 4px 20px rgba(255, 71, 117, 0.3)'
                            }}>
                                <HelpCircle size={40} color="white" />
                            </Box>
                            <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{ fontWeight: 700 }}>
                                Support Center
                            </Typography>
                            <Typography color="textSecondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                                Search our knowledge base, check your ticket history, or submit a new support request
                            </Typography>
                        </Box>
                        
                        {/* Tabs Navigation */}
                        <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                            <Tabs 
                                value={activeTab} 
                                onChange={handleTabChange}
                                variant={isMobile ? "fullWidth" : "standard"}
                                centered={!isMobile}
                                sx={{
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontSize: isMobile ? '0.875rem' : '1rem',
                                        fontWeight: 500,
                                        py: 2
                                    },
                                    '& .Mui-selected': {
                                        color: '#FF4775'
                                    },
                                    '& .MuiTabs-indicator': {
                                        backgroundColor: '#FF4775',
                                        height: 3
                                    }
                                }}
                            >
                                <Tab 
                                    icon={<BookOpen size={20} />} 
                                    iconPosition="start" 
                                    label="Knowledge Base"
                                    sx={{ gap: 1 }}
                                />
                                <Tab 
                                    icon={<History size={20} />} 
                                    iconPosition="start" 
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            My Tickets
                                            {openTicketsCount > 0 && (
                                                <Badge 
                                                    badgeContent={openTicketsCount} 
                                                    color="error"
                                                    sx={{ 
                                                        '& .MuiBadge-badge': { 
                                                            fontSize: '0.75rem',
                                                            height: 18,
                                                            minWidth: 18
                                                        } 
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                    sx={{ gap: 1 }}
                                />
                                <Tab 
                                    icon={<Plus size={20} />} 
                                    iconPosition="start" 
                                    label="New Ticket"
                                    sx={{ gap: 1 }}
                                />
                            </Tabs>
                        </Paper>
                        
                        {/* Tab Panels */}
                        <TabPanel value={activeTab} index={0}>
                            <KnowledgeBaseSearch onArticleClick={handleArticleClick} />
                        </TabPanel>
                        
                        <TabPanel value={activeTab} index={1}>
                            <TicketHistory />
                        </TabPanel>
                        
                        <TabPanel value={activeTab} index={2}>
                            {/* Show KB search prompt if user hasn't searched */}
                            {!hasSearchedKB && (
                                <Paper 
                                    sx={{ 
                                        p: 3, 
                                        mb: 3, 
                                        bgcolor: 'info.light',
                                        color: 'info.contrastText',
                                        borderRadius: 2
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <BookOpen size={24} />
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                Have you checked our Knowledge Base?
                                            </Typography>
                                            <Typography variant="body2">
                                                Many common questions can be answered instantly. 
                                                <Box
                                                    component="span"
                                                    onClick={() => setActiveTab(0)}
                                                    sx={{ 
                                                        ml: 1,
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer',
                                                        '&:hover': { opacity: 0.8 }
                                                    }}
                                                >
                                                    Search now →
                                                </Box>
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            )}
                            
                            <ZendeskForm 
                                darkMode={darkMode} 
                                onSuccess={handleTicketCreated}
                                showKBSearch={!hasSearchedKB}
                            />
                        </TabPanel>
                    </Container>
                </Box>
            </Box>
            <Footer />
        </>
    );
};

export default SupportPage;